// HelpAssistant/useBlockingModalOpen.js
//
// Is a BLOCKING modal dialog currently open anywhere in the app?
//
// WHY this exists (BUG-explore-398d6d, measured live on web-stg @390x844,
// 2026-08-15): the Help FAB renders in a `position: fixed` root with
// `z-index: 13000` pinned to the bottom-right corner (left 318, top 772 at
// 390px). A MUI Dialog's backdrop/paper stacks at z-index 1300-1600, i.e.
// BELOW the FAB's stacking layer, so the FAB paints ON TOP of a dialog and
// silently owns the pixels of any confirm button that lands in that corner —
// exactly the AddCaptureModal "Capture/Save" button. `document.elementFromPoint`
// at the button's own centre returned the FAB's launcher, and a real click on
// the button was intercepted (never fired the save). This is the SAME G-139 /
// QA-111 overlap class already fixed for the consent banner and the dismiss-X:
// a control that is visible but not clickable because the FAB owns its pixels.
//
// The fix is the same shape as the consent-banner fix — react to the obstruction
// rather than tune a z-index race. While a blocking dialog is mounted the FAB is
// hidden entirely (see index.jsx), so it can never own a dialog's pixels by
// construction, at every viewport width. When the dialog closes the FAB returns.
//
// SCOPE — what makes the FAB hide (anything it would paint on top of and steal
// the pixels/clicks from, at its fixed bottom-right corner):
//   * a MUI `Dialog` (its portal root carries `.MuiDialog-root`), OR
//   * any element with `role="dialog"` AND `aria-modal="true"` (ARIA-correct
//     app modals that are not MUI Dialogs), OR
//   * a KNOWN bottom-right-anchored overlay the FAB measurably occludes but that
//     is deliberately NON-modal, so the two selectors above miss it:
//       - the Edit-Wheel onboarding tour card
//         (`[data-testid="edit-wheel-tutorial-card"]`, `aria-modal="false"`,
//         fixed bottom-right). Measured live web-stg bundle main.bbd7179e.js
//         2026-08-15: the FAB rect {1360,820,1416,876} covered the tour's purple
//         "Start" CTA {1324,807,1391,851} by 961px^2, and
//         `document.elementFromPoint` at the CTA's right edge returned the FAB's
//         MuiFab-root, not the button (BUG-editwheel-279a2e).
//       - the version-history drawer (`.vh-drawer`, an `<aside role="dialog">`
//         with NO aria-modal, z-index 5000 < the FAB's 13000, docked full-height
//         bottom-right). Its footer ("Versions kept 90 days on P · unlimited on
//         Ps · …") sits in the same corner the FAB owns (BUG-editwheel-229fe1).
// Deliberately NOT matched: MUI Menu/Select/Popover (`.MuiPopover-root`,
// `.MuiMenu-root`, role=menu/listbox) — those do not trap the corner the way a
// full dialog does, and hiding the FAB every time a dropdown opens would flicker.
// A BARE `role="dialog"` (no aria-modal, not one of the two overlays above) also
// stays FALSE — that non-modal-dialog rule is what keeps the Help panel's own
// Card from ever hiding Help.
// ALSO excluded: the Help panel itself (`role="dialog"` on its Card) and
// anything else living inside the FAB's own root, so opening Help never hides
// Help.

import { useEffect, useState } from "react";

/** data-testid the HelpAssistant root Box carries (see index.jsx). */
export const FAB_ROOT_SELECTOR = '[data-testid="help-fab-root"]';

/** Selector for true blocking modals the FAB must never be allowed to cover. */
export const BLOCKING_MODAL_SELECTOR =
  '.MuiDialog-root, [role="dialog"][aria-modal="true"]';

/**
 * Known bottom-right-anchored overlays the FAB measurably occludes but that are
 * deliberately NON-modal, so BLOCKING_MODAL_SELECTOR misses them. Each entry is
 * backed by a measured live overlap (see the SCOPE note above):
 *   - the onboarding tour card  (BUG-editwheel-279a2e)
 *   - the version-history drawer (BUG-editwheel-229fe1)
 */
export const FAB_OCCLUDING_OVERLAY_SELECTOR =
  '[data-testid="edit-wheel-tutorial-card"], .vh-drawer';

/** Everything that makes the FAB hide: blocking modals + occluding overlays. */
export const FAB_HIDE_SELECTOR = `${BLOCKING_MODAL_SELECTOR}, ${FAB_OCCLUDING_OVERLAY_SELECTOR}`;

/**
 * Decorative motion layers (e.g. the Login Nebula) carry this marker. They write inline
 * transform/opacity every animation frame and can never open or close a modal, so the
 * watcher below skips mutation batches that happen entirely inside one. Without this the
 * watcher ran querySelectorAll + getComputedStyle on every login-animation frame (Gordon
 * 2026-09-16 crescent lag; measured ~0.45-0.75 s per 5 s at 4x CPU throttle on web-dev).
 */
export const MOTION_LAYER_SELECTOR = "[data-motion-layer]";

const insideMotionLayer = (node) => {
  const el = node && node.nodeType === 1 ? node : node && node.parentElement;
  return !!(el && typeof el.closest === "function" && el.closest(MOTION_LAYER_SELECTOR));
};

/**
 * Can this MutationObserver batch change whether a modal is open? False only when EVERY
 * record targets a node inside a decorative motion layer. A missing batch fails open (true).
 */
export const mutationsAffectModals = (records) => {
  if (!records || !records.length) return true;
  for (let i = 0; i < records.length; i += 1) {
    if (!insideMotionLayer(records[i].target)) return true;
  }
  return false;
};

/** True unless the element is explicitly hidden (display:none / visibility:hidden). */
const isNotHidden = (el) => {
  try {
    const cs =
      typeof window !== "undefined" && window.getComputedStyle
        ? window.getComputedStyle(el)
        : null;
    if (!cs) return true;
    return cs.display !== "none" && cs.visibility !== "hidden";
  } catch (e) {
    return true;
  }
};

/**
 * Pure DOM predicate — exported so a unit test asserts the SAME arithmetic the
 * hook runs. Returns true when at least one blocking modal is open OUTSIDE the
 * Help FAB's own subtree.
 *
 * `ignoreSelector` (optional) excludes matching elements from counting as
 * blocking — e.g. the GDPR consent banner, which is a genuine
 * `[role="dialog"][aria-modal="true"]` surface (so it must stay in
 * BLOCKING_MODAL_SELECTOR for callers like the Share sheet check) but is
 * bottom-docked rather than a true full-screen/occluding overlay, so a caller
 * whose own element sits BELOW its z-index (never paints over it) can react to
 * it by repositioning instead of hiding. See GetAppButton/index.jsx.
 */
export const isBlockingModalOpen = (doc, ignoreSelector) => {
  const root =
    doc || (typeof document !== "undefined" ? document : null);
  if (!root || typeof root.querySelectorAll !== "function") return false;
  const fab = root.querySelector(FAB_ROOT_SELECTOR);
  const candidates = root.querySelectorAll(FAB_HIDE_SELECTOR);
  for (let i = 0; i < candidates.length; i += 1) {
    const el = candidates[i];
    if (ignoreSelector && el.matches(ignoreSelector)) continue;
    // Ignore the Help panel and anything within the FAB's own root — opening
    // Help must never hide Help.
    if (fab && (fab === el || fab.contains(el) || el.contains(fab))) continue;
    if (!isNotHidden(el)) continue;
    return true;
  }
  return false;
};

/**
 * Live boolean: is a blocking modal dialog open right now? Reads synchronously
 * on mount (so a modal already open at mount is caught) and then tracks
 * open/close via a MutationObserver on document.body.
 *
 * @param {string} [ignoreSelector] see isBlockingModalOpen above. Omitted by
 * every existing caller (Help FAB, Share-sheet suppression), so their
 * behaviour is unchanged.
 */
export default function useBlockingModalOpen(ignoreSelector) {
  const [openModal, setOpenModal] = useState(() =>
    isBlockingModalOpen(undefined, ignoreSelector),
  );

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const recompute = () =>
      setOpenModal((prev) => {
        const next = isBlockingModalOpen(undefined, ignoreSelector);
        return next === prev ? prev : next;
      });
    // Catch a modal that opened between the initial render and this effect.
    recompute();
    const observer =
      typeof MutationObserver === "function"
        ? new MutationObserver((records) => {
            if (mutationsAffectModals(records)) recompute();
          })
        : null;
    if (observer) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["role", "aria-modal", "class", "style"],
      });
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, [ignoreSelector]);

  return openModal;
}
